import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Plus, Eye, Printer, CheckCircle, Trash2, Edit, ChevronLeft, ChevronRight, Calendar, Filter, DollarSign, Users, Download, FileText, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get cut-off periods - Simple 1-15 and 16-EOM pattern
const getCutoffPeriods = () => {
  const periods = [];
  const today = new Date();
  
  // Determine current half based on today's date
  const currentDay = today.getDate();
  const currentHalf = currentDay <= 15 ? 'first' : 'second';
  
  // Generate last 12 periods (6 months back = 12 half-months)
  let workingDate = new Date(today);
  let workingHalf = currentHalf;
  
  for (let i = 0; i < 12; i++) {
    const year = workingDate.getFullYear();
    const month = workingDate.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const monthName = workingDate.toLocaleString('en', { month: 'short' });
    
    let label, start, end;
    
    if (workingHalf === 'first') {
      label = `${monthName} 1-15, ${year}`;
      start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      end = `${year}-${String(month + 1).padStart(2, '0')}-15`;
    } else {
      label = `${monthName} 16-${lastDay}, ${year}`;
      start = `${year}-${String(month + 1).padStart(2, '0')}-16`;
      end = `${year}-${String(month + 1).padStart(2, '0')}-${lastDay}`;
    }
    
    periods.push({ label, start, end, key: start });
    
    // Move to previous period
    if (workingHalf === 'first') {
      // Go to previous month's second half
      workingDate = new Date(year, month - 1, 1);
      workingHalf = 'second';
    } else {
      // Go to same month's first half
      workingHalf = 'first';
    }
  }
  
  return periods;
};

const Payslips = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const username = user?.username?.toLowerCase() || '';
  const fullName = user?.full_name?.toLowerCase() || '';
  
  // Payroll managers can see draft payslips and send to employees
  const isPayrollManager = isAdmin || 
    username.includes('alma') || fullName.includes('alma') ||
    username.includes('pabs') || fullName.includes('pabs') ||
    username.includes('arbe') || fullName.includes('arbe');
  
  // Alma and Arbe can create payslips
  const canCreatePayslip = isAdmin || username.includes('alma') || fullName.includes('alma') || username.includes('arbe') || fullName.includes('arbe');
  
  const [payslips, setPayslips] = useState([]);
  const [pendingPayslips, setPendingPayslips] = useState([]);
  const [approvedTimesheets, setApprovedTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('by-period');
  const [sendingPayslip, setSendingPayslip] = useState(null);
  
  // Period selection
  const periods = useMemo(() => getCutoffPeriods(), []);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);
  const selectedPeriod = periods[selectedPeriodIndex];
  
  const [formData, setFormData] = useState({
    timesheet_id: '',
    half_month_salary: '',
    overtime_pay: '0',
    holiday_pay: '0',
    bonus: '0',
    sss: '0',
    philhealth: '0',
    pagibig: '0',
    tax: '0',
    cash_advance: '0',
    other_deductions: '0',
    absence_deduction: '0',
    overtime_notes: '',
    net_pay_override: ''  // Empty means auto-calculate, value means manual override
  });
  
  // CA integration state
  const [pendingCA, setPendingCA] = useState(null);
  const [loadingCA, setLoadingCA] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  
  // Approved overtime state
  const [approvedOvertime, setApprovedOvertime] = useState(null);
  const [loadingOvertime, setLoadingOvertime] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/payslips`),
        canCreatePayslip ? axios.get(`${API}/timesheets`) : Promise.resolve({ data: [] }),
        canCreatePayslip ? axios.get(`${API}/timesheets-v2`) : Promise.resolve({ data: [] })
      ];
      
      // Payroll managers can see pending payslips
      if (isPayrollManager) {
        requests.push(axios.get(`${API}/payslips/pending/review`));
      }
      
      const results = await Promise.all(requests);
      const [payslipsRes, timesheetsRes, timesheetsV2Res] = results;
      
      setPayslips(payslipsRes.data || []);
      
      if (isPayrollManager && results[3]) {
        setPendingPayslips(results[3].data || []);
      }
      
      // Combine timesheets from both v1 and v2 collections
      const allTimesheets = [
        ...(timesheetsRes.data || []),
        ...(timesheetsV2Res.data || [])
      ];
      
      // Filter for approved timesheets that don't have payslips yet
      const existingPayslipTimesheetIds = (payslipsRes.data || []).map(p => p.timesheet_id);
      const approved = allTimesheets.filter(
        ts => ts.status === 'approved' && !existingPayslipTimesheetIds.includes(ts.id)
      );
      setApprovedTimesheets(approved);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  // Filter payslips by selected period
  const filteredPayslips = useMemo(() => {
    if (!selectedPeriod) return payslips;
    return payslips.filter(p => {
      const pStart = p.period_start?.split('T')[0];
      return pStart === selectedPeriod.start;
    });
  }, [payslips, selectedPeriod]);

  // Group payslips by period for summary
  const payslipsByPeriod = useMemo(() => {
    const grouped = {};
    payslips.forEach(p => {
      const key = p.period_start?.split('T')[0];
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(p);
    });
    return grouped;
  }, [payslips]);

  // Calculate summary for selected period
  const periodSummary = useMemo(() => {
    const total = filteredPayslips.reduce((acc, p) => ({
      gross: acc.gross + (p.gross_pay || 0),
      deductions: acc.deductions + (p.total_deductions || 0),
      net: acc.net + (p.net_pay || 0),
      count: acc.count + 1
    }), { gross: 0, deductions: 0, net: 0, count: 0 });
    return total;
  }, [filteredPayslips]);

  const resetForm = () => {
    setFormData({
      timesheet_id: '',
      half_month_salary: '',
      overtime_pay: '0',
      holiday_pay: '0',
      bonus: '0',
      sss: '0',
      philhealth: '0',
      pagibig: '0',
      tax: '0',
      cash_advance: '0',
      other_deductions: '0',
      absence_deduction: '0',
      overtime_notes: '',
      net_pay_override: ''
    });
    setEditMode(false);
    setSelectedPayslip(null);
    setPendingCA(null);
    setSelectedUserId(null);
    setApprovedOvertime(null);
  };

  // Send payslip to employee
  const sendPayslipToEmployee = async (payslipId) => {
    setSendingPayslip(payslipId);
    try {
      await axios.post(`${API}/payslips/${payslipId}/send`);
      toast.success('Payslip sent to employee!');
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error sending payslip:', error);
      toast.error(error.response?.data?.detail || 'Failed to send payslip');
    } finally {
      setSendingPayslip(null);
    }
  };

  // Edit payslip (save draft changes)
  const savePayslipEdit = async () => {
    if (!selectedPayslip) return;
    
    try {
      const editData = {
        half_month_salary: parseFloat(formData.half_month_salary) || 0,
        overtime_pay: parseFloat(formData.overtime_pay) || 0,
        overtime_notes: formData.overtime_notes || '',
        holiday_pay: parseFloat(formData.holiday_pay) || 0,
        bonus: parseFloat(formData.bonus) || 0,
        absence_deduction: parseFloat(formData.absence_deduction) || 0,
        sss: parseFloat(formData.sss) || 0,
        philhealth: parseFloat(formData.philhealth) || 0,
        pagibig: parseFloat(formData.pagibig) || 0,
        tax: parseFloat(formData.tax) || 0,
        cash_advance: parseFloat(formData.cash_advance) || 0,
        other_deductions: parseFloat(formData.other_deductions) || 0
      };
      
      await axios.put(`${API}/payslips/${selectedPayslip.id}/edit`, editData);
      toast.success('Payslip updated successfully!');
      setEditMode(false);
      setShowViewDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error updating payslip:', error);
      toast.error(error.response?.data?.detail || 'Failed to update payslip');
    }
  };

  // Fetch pending CA deduction for selected user - uses total-balance endpoint
  const fetchPendingCA = useCallback(async (userId) => {
    if (!userId) {
      setPendingCA(null);
      return;
    }
    
    setLoadingCA(true);
    try {
      // Use total-balance endpoint for comprehensive CA info
      const res = await axios.get(`${API}/cash-advance/user/${userId}/total-balance`);
      setPendingCA({
        total_ca_deduction: res.data.total_ca_balance || 0,
        ca_count: res.data.active_ca_count || 0,
        user_name: res.data.user_name,
        active_cas: res.data.active_cas || []
      });
      
      // Don't auto-fill - let Arbe/Pabs decide the deduction amount manually
      // This follows the user's requirement that admins manually enter the deduction
    } catch (error) {
      console.error('Error fetching pending CA:', error);
      // Fallback to the old endpoint if new one fails
      try {
        const res = await axios.get(`${API}/cash-advance/user/${userId}/pending-deduction-summary`);
        setPendingCA(res.data);
      } catch (e) {
        setPendingCA(null);
      }
    } finally {
      setLoadingCA(false);
    }
  }, []);

  // Fetch approved overtime for selected user and period
  const fetchApprovedOvertime = useCallback(async (userId, periodStart, periodEnd) => {
    if (!userId || !periodStart || !periodEnd) {
      setApprovedOvertime(null);
      return;
    }
    
    setLoadingOvertime(true);
    try {
      // Use the new calculate endpoint that handles both salary types
      const res = await axios.get(`${API}/payslips/calculate/${userId}`, {
        params: { period_start: periodStart, period_end: periodEnd }
      });
      setApprovedOvertime(res.data);
      
      // Auto-fill based on salary type
      if (res.data.salary_type === 'daily') {
        // Daily worker: use calculated basic_pay (days × daily_rate)
        setFormData(prev => ({
          ...prev,
          half_month_salary: String(res.data.basic_pay || 0),
          overtime_pay: String(res.data.overtime_pay || 0)
        }));
      } else {
        // Half-month salary: use the stored salary, auto-fill overtime
        setFormData(prev => ({
          ...prev,
          half_month_salary: String(res.data.half_month_salary || prev.half_month_salary || 0),
          overtime_pay: String(res.data.overtime_pay || 0)
        }));
      }
    } catch (error) {
      console.error('Error fetching payslip calculation:', error);
      setApprovedOvertime(null);
    } finally {
      setLoadingOvertime(false);
    }
  }, []);

  // When timesheet is selected, fetch the user's pending CA and approved overtime
  const handleTimesheetSelect = useCallback((timesheetId) => {
    setFormData(prev => ({ ...prev, timesheet_id: timesheetId }));
    
    const selectedTimesheet = approvedTimesheets.find(ts => ts.id === timesheetId);
    if (selectedTimesheet) {
      setSelectedUserId(selectedTimesheet.user_id);
      fetchPendingCA(selectedTimesheet.user_id);
      
      // Fetch approved overtime for this user and period
      const periodStart = selectedTimesheet.period_start?.split('T')[0];
      const periodEnd = selectedTimesheet.period_end?.split('T')[0];
      if (periodStart && periodEnd) {
        fetchApprovedOvertime(selectedTimesheet.user_id, periodStart, periodEnd);
      }
    }
  }, [approvedTimesheets, fetchPendingCA, fetchApprovedOvertime]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      timesheet_id: editMode ? selectedPayslip.timesheet_id : formData.timesheet_id,
      half_month_salary: parseFloat(formData.half_month_salary) || 0,
      overtime_pay: parseFloat(formData.overtime_pay) || 0,
      overtime_notes: formData.overtime_notes || '',
      holiday_pay: parseFloat(formData.holiday_pay) || 0,
      bonus: parseFloat(formData.bonus) || 0,
      absence_deduction: parseFloat(formData.absence_deduction) || 0,
      sss: parseFloat(formData.sss) || 0,
      philhealth: parseFloat(formData.philhealth) || 0,
      pagibig: parseFloat(formData.pagibig) || 0,
      tax: parseFloat(formData.tax) || 0,
      cash_advance: parseFloat(formData.cash_advance) || 0,
      other_deductions: parseFloat(formData.other_deductions) || 0,
      net_pay_override: formData.net_pay_override ? parseFloat(formData.net_pay_override) : null
    };

    try {
      let response;
      if (editMode) {
        response = await axios.put(`${API}/payslips/${selectedPayslip.id}`, payload);
        if (response.data?.ca_deduction_recorded && payload.cash_advance > 0) {
          toast.success(`Payslip updated! CA deduction of ${formatCurrency(payload.cash_advance)} recorded to CA module.`);
        } else {
          toast.success('Payslip updated successfully');
        }
      } else {
        response = await axios.post(`${API}/payslips`, payload);
        if (response.data?.ca_deduction_recorded && payload.cash_advance > 0) {
          toast.success(`Payslip created! CA deduction of ${formatCurrency(payload.cash_advance)} recorded to CA module.`);
        } else {
          toast.success('Payslip created successfully');
        }
      }
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payslip');
    }
  };

  const handleView = (payslip) => {
    setSelectedPayslip(payslip);
    setShowViewDialog(true);
  };

  const handleEdit = (payslip) => {
    setSelectedPayslip(payslip);
    setFormData({
      timesheet_id: payslip.timesheet_id,
      half_month_salary: String(payslip.half_month_salary || 0),
      overtime_pay: String(payslip.overtime_pay || 0),
      holiday_pay: String(payslip.holiday_pay || 0),
      bonus: String(payslip.bonus || 0),
      sss: String(payslip.sss || 0),
      philhealth: String(payslip.philhealth || 0),
      pagibig: String(payslip.pagibig || 0),
      tax: String(payslip.tax || 0),
      cash_advance: String(payslip.cash_advance || 0),
      other_deductions: String(payslip.other_deductions || 0),
      absence_deduction: String(payslip.absence_deduction || 0),
      overtime_notes: payslip.overtime_notes || '',
      net_pay_override: payslip.net_pay_override ? String(payslip.net_pay_override) : ''
    });
    setEditMode(true);
    setShowCreateDialog(true);
    
    // Fetch pending CA and approved overtime for this user
    if (payslip.user_id) {
      setSelectedUserId(payslip.user_id);
      fetchPendingCA(payslip.user_id);
      
      // Fetch approved overtime for the payslip period
      const periodStart = payslip.period_start?.split('T')[0];
      const periodEnd = payslip.period_end?.split('T')[0];
      if (periodStart && periodEnd) {
        fetchApprovedOvertime(payslip.user_id, periodStart, periodEnd);
      }
    }
  };

  const handleFinalize = async (payslipId) => {
    if (!window.confirm('Are you sure you want to finalize this payslip? This action cannot be undone. Any Cash Advance deductions will be automatically recorded.')) return;
    try {
      const response = await axios.post(`${API}/payslips/${payslipId}/finalize`);
      if (response.data.ca_deduction_recorded) {
        toast.success(`Payslip finalized! CA deduction of ${formatCurrency(response.data.ca_deduction_amount)} recorded.`);
      } else {
        toast.success('Payslip finalized');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize payslip');
    }
  };

  const handleDelete = async (payslipId) => {
    if (!window.confirm('Are you sure you want to delete this payslip?')) return;
    try {
      await axios.delete(`${API}/payslips/${payslipId}`);
      toast.success('Payslip deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payslip');
    }
  };

  const handlePrint = (payslip) => {
    setSelectedPayslip(payslip);
    setShowViewDialog(true);
    setTimeout(() => window.print(), 500);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-PH', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `₱${parseFloat(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
  };

  const handleExportExcel = () => {
    const url = `${API}/payslips/export/excel?period_start=${selectedPeriod.start}&period_end=${selectedPeriod.end}`;
    window.open(url, '_blank');
    toast.success('Exporting payslips to Excel...');
  };

  const handleExportPdf = (payslipId) => {
    const url = `${API}/payslips/${payslipId}/export/pdf`;
    window.open(url, '_blank');
    toast.success('Exporting payslip to PDF...');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Payslips</h2>
          <p className="text-slate-600 mt-1">
            {canCreatePayslip ? 'Manage payslips by cut-off period' : 'View your payslips'}
          </p>
        </div>
        <div className="flex gap-2">
          {canCreatePayslip && payslips.length > 0 && (
            <Button 
              variant="outline"
              className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
              onClick={handleExportExcel}
              data-testid="export-excel-btn"
            >
              <Download size={18} className="mr-2" />
              Export Excel
            </Button>
          )}
          {canCreatePayslip && approvedTimesheets.length > 0 && (
            <Button 
              className="bg-slate-900 hover:bg-slate-800"
              onClick={() => {
                resetForm();
                setShowCreateDialog(true);
              }}
              data-testid="create-payslip-btn"
            >
              <Plus size={20} className="mr-2" />
              Create Payslip
            </Button>
          )}
        </div>
      </div>

      {/* Period Selector */}
      <Card className="bg-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedPeriodIndex(Math.min(selectedPeriodIndex + 1, periods.length - 1))}
              disabled={selectedPeriodIndex >= periods.length - 1}
            >
              <ChevronLeft size={18} />
            </Button>
            
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-slate-500" />
              <span className="font-semibold text-lg">{selectedPeriod?.label}</span>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedPeriodIndex(Math.max(selectedPeriodIndex - 1, 0))}
              disabled={selectedPeriodIndex <= 0}
            >
              <ChevronRight size={18} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Payslips</p>
                <p className="text-xl font-bold">{periodSummary.count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <DollarSign className="text-slate-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Gross Pay</p>
                <p className="text-lg font-bold">{formatCurrency(periodSummary.gross)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <DollarSign className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Deductions</p>
                <p className="text-lg font-bold text-red-600">{formatCurrency(periodSummary.deductions)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Net Pay</p>
                <p className="text-lg font-bold text-green-600">{formatCurrency(periodSummary.net)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payslips Table */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter size={18} />
            Payslips for {selectedPeriod?.label}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Employee</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Company</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Gross</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Deductions</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Net Pay</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredPayslips.length > 0 ? filteredPayslips.map((payslip) => (
                  <tr key={payslip.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{payslip.user_name}</td>
                    <td className="px-4 py-3 text-slate-600 text-sm">{payslip.company_name || '-'}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{formatCurrency(payslip.gross_pay)}</td>
                    <td className="px-4 py-3 text-right text-red-600">{formatCurrency(payslip.total_deductions)}</td>
                    <td className="px-4 py-3 text-right font-bold text-green-600">{formatCurrency(payslip.net_pay)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        payslip.status === 'sent' || payslip.status === 'finalized' || payslip.status === 'signed'
                          ? 'bg-green-100 text-green-700'
                          : payslip.status === 'draft' || !payslip.status
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {payslip.status === 'draft' || !payslip.status ? 'Pending Review' : payslip.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => handleView(payslip)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="View"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleExportPdf(payslip.id)}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"
                          title="Download PDF"
                        >
                          <FileText size={16} />
                        </button>
                        <button
                          onClick={() => handlePrint(payslip)}
                          className="p-2 text-slate-600 hover:bg-slate-50 rounded-lg"
                          title="Print"
                        >
                          <Printer size={16} />
                        </button>
                        {/* Edit button - Admin can edit any stage, others only drafts */}
                        {(isAdmin || (isPayrollManager && (payslip.status === 'draft' || !payslip.status))) && (
                          <button
                            onClick={() => handleEdit(payslip)}
                            className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
                            title={isAdmin ? "Edit Payslip (Admin)" : "Edit Payslip"}
                          >
                            <Edit size={16} />
                          </button>
                        )}
                        {/* Send button - only for payroll managers on draft payslips */}
                        {isPayrollManager && (payslip.status === 'draft' || !payslip.status) && (
                          <button
                            onClick={() => sendPayslipToEmployee(payslip.id)}
                            disabled={sendingPayslip === payslip.id}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg disabled:opacity-50"
                            title="Send to Employee"
                          >
                            {sendingPayslip === payslip.id ? (
                              <RefreshCw size={16} className="animate-spin" />
                            ) : (
                              <CheckCircle size={16} />
                            )}
                          </button>
                        )}
                        {/* Delete button - Admin can delete any, others only drafts */}
                        {(isAdmin || (isPayrollManager && (payslip.status === 'draft' || !payslip.status))) && (
                          <button
                            onClick={() => handleDelete(payslip.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center text-slate-500">
                      No payslips for this period
                    </td>
                  </tr>
                )}
              </tbody>
              {filteredPayslips.length > 0 && (
                <tfoot className="bg-slate-100">
                  <tr className="font-semibold">
                    <td colSpan="2" className="px-4 py-3 text-right">TOTAL:</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(periodSummary.gross)}</td>
                    <td className="px-4 py-3 text-right text-red-600">{formatCurrency(periodSummary.deductions)}</td>
                    <td className="px-4 py-3 text-right text-green-600">{formatCurrency(periodSummary.net)}</td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Payslip Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit Payslip' : 'Create Payslip'}</DialogTitle>
            <DialogDescription>
              {editMode ? 'Update payslip details' : 'Create a payslip from an approved timesheet'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editMode && (
              <div>
                <Label>Select Approved Timesheet *</Label>
                <Select value={formData.timesheet_id} onValueChange={handleTimesheetSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select timesheet..." />
                  </SelectTrigger>
                  <SelectContent>
                    {approvedTimesheets.map((ts) => (
                      <SelectItem key={ts.id} value={ts.id}>
                        {ts.user_name} - {formatDate(ts.period_start)} to {formatDate(ts.period_end)} ({ts.total_days} days)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* CA Balance Alert - Prominent display when employee has outstanding CA */}
            {pendingCA && pendingCA.total_ca_deduction > 0 && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-4 animate-pulse-once" data-testid="ca-balance-alert">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="text-white" size={24} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-amber-800 text-lg flex items-center gap-2">
                      <AlertCircle size={18} />
                      Cash Advance Balance
                    </h4>
                    <p className="text-2xl font-bold text-amber-900 mt-1">
                      {formatCurrency(pendingCA.total_ca_deduction)}
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      {pendingCA.user_name} has <strong>{pendingCA.ca_count}</strong> active cash advance(s)
                    </p>
                    {pendingCA.active_cas && pendingCA.active_cas.length > 0 && (
                      <div className="mt-3 space-y-1">
                        <p className="text-xs font-semibold text-amber-800">Active Advances:</p>
                        {pendingCA.active_cas.slice(0, 3).map((ca, idx) => (
                          <div key={idx} className="text-xs text-amber-700 bg-white/50 rounded px-2 py-1">
                            {ca.reason}: <strong>{formatCurrency(ca.amount_remaining)}</strong> remaining
                          </div>
                        ))}
                        {pendingCA.active_cas.length > 3 && (
                          <p className="text-xs text-amber-600 italic">+{pendingCA.active_cas.length - 3} more...</p>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-amber-600 mt-2 bg-amber-100 rounded px-2 py-1 inline-block">
                      💡 Enter deduction amount below manually
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* No CA Balance indicator */}
            {pendingCA && pendingCA.total_ca_deduction === 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2" data-testid="no-ca-alert">
                <CheckCircle size={18} className="text-green-600" />
                <p className="text-sm text-green-700">
                  <strong>{pendingCA.user_name}</strong> has no outstanding cash advances
                </p>
              </div>
            )}
            
            <div>
              <Label>{approvedOvertime?.salary_type === 'daily' ? 'Daily Salary (₱) *' : 'Basic Salary (₱) *'}</Label>
              <Input
                type="number"
                value={formData.half_month_salary}
                onChange={(e) => setFormData({ ...formData, half_month_salary: e.target.value })}
                required
                placeholder="0.00"
              />
              {/* Show salary type info */}
              {approvedOvertime && approvedOvertime.salary_type === 'daily' ? (
                <div className="mt-1 p-2 bg-green-50 border border-green-200 rounded text-xs">
                  <p className="font-medium text-green-700">Daily Worker</p>
                  <p className="text-green-600">
                    {approvedOvertime.full_days || 0} full days + {approvedOvertime.partial_days || 0} partial = <strong>{approvedOvertime.days_worked}</strong> days
                  </p>
                  <p className="text-green-600">
                    {approvedOvertime.days_worked} days × ₱{approvedOvertime.daily_rate}/day = {formatCurrency(approvedOvertime.basic_pay)}
                  </p>
                  {approvedOvertime.total_undertime_hours > 0 && (
                    <p className="text-amber-600 mt-1">
                      ⚠️ Undertime: {approvedOvertime.total_undertime_hours} hrs (partial days deducted)
                    </p>
                  )}
                  {approvedOvertime.timesheet_days > 0 && approvedOvertime.timesheet_days !== Math.round(approvedOvertime.days_worked) && (
                    <div className="text-red-600 mt-1">
                      <p>⚠️ Timesheet shows {approvedOvertime.timesheet_days} days but calculated {approvedOvertime.days_worked} days from timeclock</p>
                      {approvedOvertime.missing_clock_out_dates?.length > 0 && (
                        <p className="text-xs">Missing clock-out: {approvedOvertime.missing_clock_out_dates.join(', ')}</p>
                      )}
                    </div>
                  )}
                  {parseFloat(formData.half_month_salary) !== approvedOvertime.basic_pay && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, half_month_salary: String(approvedOvertime.basic_pay) }))}
                      className="mt-1 text-green-600 hover:text-green-800 underline font-medium"
                    >
                      Use calculated amount
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-500 mt-1">Half month salary (1-15 or 16-EOM cut-off)</p>
              )}
              
              {/* Show Absent and Half Day Deductions */}
              {approvedOvertime && (approvedOvertime.absent_days > 0 || approvedOvertime.half_days > 0) && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs">
                  <p className="font-semibold text-red-700 mb-1">Deductions (Absent & Half Days)</p>
                  <div className="flex gap-4">
                    {approvedOvertime.absent_days > 0 && (
                      <div>
                        <span className="text-orange-600 font-medium">Absent (A):</span>
                        <span className="ml-1 font-bold">{approvedOvertime.absent_days} days</span>
                      </div>
                    )}
                    {approvedOvertime.half_days > 0 && (
                      <div>
                        <span className="text-purple-600 font-medium">Half Days (H):</span>
                        <span className="ml-1 font-bold">{approvedOvertime.half_days} × 0.5 = {approvedOvertime.half_days * 0.5} days</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-1 pt-1 border-t border-red-200">
                    <span className="text-red-700 font-medium">Total Deduction:</span>
                    <span className="ml-1 font-bold text-red-800">
                      {approvedOvertime.total_deduction_days} days = {formatCurrency(approvedOvertime.deduction_amount)}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Overtime Pay (₱)</Label>
                <Input
                  type="number"
                  value={formData.overtime_pay}
                  onChange={(e) => setFormData({ ...formData, overtime_pay: e.target.value })}
                  placeholder="0.00"
                />
                {/* Show overtime info based on salary type */}
                {loadingOvertime && (
                  <p className="text-xs text-slate-500 mt-1">Loading overtime data...</p>
                )}
                {approvedOvertime && !loadingOvertime && approvedOvertime.salary_type === 'daily' && (
                  <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs">
                    <p className="font-medium text-orange-700">Daily Worker OT</p>
                    <p className="text-orange-600">
                      {approvedOvertime.total_overtime_hours} hrs over 8hr/day × ₱{approvedOvertime.overtime_rate}/hr
                    </p>
                    <p className="font-bold text-orange-800">{formatCurrency(approvedOvertime.overtime_pay)}</p>
                    {parseFloat(formData.overtime_pay) !== approvedOvertime.overtime_pay && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, overtime_pay: String(approvedOvertime.overtime_pay) }))}
                        className="mt-1 text-orange-600 hover:text-orange-800 underline font-medium"
                      >
                        Use calculated amount
                      </button>
                    )}
                  </div>
                )}
                {approvedOvertime && !loadingOvertime && approvedOvertime.salary_type !== 'daily' && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-medium text-blue-700">Approved Overtime:</span>
                        <span className="ml-1">{approvedOvertime.approved_overtime_hours || 0} hrs × ₱{approvedOvertime.overtime_rate}/hr</span>
                      </div>
                      <span className="font-bold text-blue-800">{formatCurrency(approvedOvertime.overtime_pay)}</span>
                    </div>
                    {parseFloat(formData.overtime_pay) !== approvedOvertime.overtime_pay && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, overtime_pay: String(approvedOvertime.overtime_pay) }))}
                        className="mt-1 text-blue-600 hover:text-blue-800 underline font-medium"
                      >
                        Use calculated amount
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div>
                <Label>Overtime Explanation *</Label>
                <Textarea
                  value={formData.overtime_notes}
                  onChange={(e) => setFormData({ ...formData, overtime_notes: e.target.value })}
                  placeholder="Explain the overtime here, e.g.&#10;- Sunday work: 4 hours&#10;- Holiday shift: 8 hours&#10;- Night differential: 3 hours"
                  className="text-sm min-h-[80px]"
                  rows={3}
                />
                <p className="text-xs text-slate-500 mt-1">This explanation will appear on the employee's payslip</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Holiday Pay (₱)</Label>
                <Input
                  type="number"
                  value={formData.holiday_pay}
                  onChange={(e) => setFormData({ ...formData, holiday_pay: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Bonus (₱)</Label>
                <Input
                  type="number"
                  value={formData.bonus}
                  onChange={(e) => setFormData({ ...formData, bonus: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <hr className="my-4" />
            <h4 className="font-semibold text-sm text-red-600">Deductions</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>SSS (₱)</Label>
                <Input
                  type="number"
                  value={formData.sss}
                  onChange={(e) => setFormData({ ...formData, sss: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>PhilHealth (₱)</Label>
                <Input
                  type="number"
                  value={formData.philhealth}
                  onChange={(e) => setFormData({ ...formData, philhealth: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Pag-IBIG (₱)</Label>
                <Input
                  type="number"
                  value={formData.pagibig}
                  onChange={(e) => setFormData({ ...formData, pagibig: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Tax (₱)</Label>
                <Input
                  type="number"
                  value={formData.tax}
                  onChange={(e) => setFormData({ ...formData, tax: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  Cash Advance Deduction (₱)
                  {loadingCA && <RefreshCw size={14} className="animate-spin text-blue-500" />}
                </Label>
                <Input
                  type="number"
                  value={formData.cash_advance}
                  onChange={(e) => setFormData({ ...formData, cash_advance: e.target.value })}
                  placeholder="0.00"
                  className={pendingCA && pendingCA.total_ca_deduction > 0 ? "border-amber-400 border-2 bg-amber-50" : ""}
                  data-testid="ca-deduction-input"
                />
                {pendingCA && pendingCA.total_ca_deduction > 0 && (
                  <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded-md">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-amber-700 text-xs font-medium">
                        <AlertCircle size={12} />
                        Total Balance: {formatCurrency(pendingCA.total_ca_deduction)}
                      </div>
                      <button 
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, cash_advance: String(pendingCA.total_ca_deduction) }))}
                        className="text-xs bg-amber-600 text-white px-2 py-0.5 rounded hover:bg-amber-700"
                      >
                        Deduct Full
                      </button>
                    </div>
                    <p className="text-xs text-amber-600 mt-1">
                      Enter the deduction amount manually. {pendingCA.ca_count} active advance(s).
                    </p>
                    {/* Live preview: remaining CA after this period's deduction */}
                    {(() => {
                      const ded = parseFloat(formData.cash_advance) || 0;
                      const remaining = Math.max(0, (pendingCA.total_ca_deduction || 0) - ded);
                      return (
                        <div className="mt-2 pt-2 border-t border-amber-200 flex items-center justify-between text-xs">
                          <span className="text-amber-700">↳ Remaining after this payslip</span>
                          <span className="font-bold text-amber-900">{formatCurrency(remaining)}</span>
                        </div>
                      );
                    })()}
                  </div>
                )}
                {pendingCA && pendingCA.total_ca_deduction === 0 && (
                  <p className="text-xs text-green-600 mt-1">✓ No pending cash advances</p>
                )}
              </div>
              <div>
                <Label>Absence Deduction (₱)</Label>
                <Input
                  type="number"
                  value={formData.absence_deduction}
                  onChange={(e) => setFormData({ ...formData, absence_deduction: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Other Deductions (₱)</Label>
                <Input
                  type="number"
                  value={formData.other_deductions}
                  onChange={(e) => setFormData({ ...formData, other_deductions: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
            
            {/* Net Pay Override Section */}
            <hr className="my-4" />
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <h4 className="font-semibold text-sm text-emerald-700 mb-3">Net Pay</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Calculated Net Pay:</span>
                  <span className="font-bold text-emerald-700">
                    {formatCurrency(
                      (parseFloat(formData.half_month_salary) || 0) +
                      (parseFloat(formData.overtime_pay) || 0) +
                      (parseFloat(formData.holiday_pay) || 0) +
                      (parseFloat(formData.bonus) || 0) -
                      (parseFloat(formData.absence_deduction) || 0) -
                      (parseFloat(formData.sss) || 0) -
                      (parseFloat(formData.philhealth) || 0) -
                      (parseFloat(formData.pagibig) || 0) -
                      (parseFloat(formData.tax) || 0) -
                      (parseFloat(formData.cash_advance) || 0) -
                      (parseFloat(formData.other_deductions) || 0)
                    )}
                  </span>
                </div>
                <div>
                  <Label className="text-emerald-700">Override Net Pay (₱) - Leave blank for auto-calculate</Label>
                  <Input
                    type="number"
                    value={formData.net_pay_override}
                    onChange={(e) => setFormData({ ...formData, net_pay_override: e.target.value })}
                    placeholder="Leave blank to use calculated amount"
                    className="border-emerald-300 focus:border-emerald-500"
                  />
                  {formData.net_pay_override && (
                    <div className="mt-1 flex items-center justify-between">
                      <p className="text-xs text-emerald-600">
                        ✓ Net Pay will be set to: {formatCurrency(parseFloat(formData.net_pay_override))}
                      </p>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, net_pay_override: '' }))}
                        className="text-xs text-red-500 hover:text-red-700 underline"
                      >
                        Clear override
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800">
                {editMode ? 'Update Payslip' : 'Create Payslip'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Payslip Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Payslip Details</DialogTitle>
          </DialogHeader>
          {selectedPayslip && (
            <div className="space-y-4 print-payslip">
              <div className="text-center border-b pb-4">
                <h3 className="font-bold text-lg">{selectedPayslip.company_name || 'COMPANY'}</h3>
                <p className="text-sm text-slate-500">PAYSLIP</p>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-slate-500">Employee:</p>
                  <p className="font-medium">{selectedPayslip.user_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Period:</p>
                  <p className="font-medium">{formatDate(selectedPayslip.period_start)} - {formatDate(selectedPayslip.period_end)}</p>
                </div>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-green-700 mb-2">Earnings</h4>
                <div className="flex justify-between text-sm">
                  <span>Basic Salary</span>
                  <span>{formatCurrency(selectedPayslip.half_month_salary)}</span>
                </div>
                {selectedPayslip.overtime_pay > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Overtime Pay</span>
                    <span>{formatCurrency(selectedPayslip.overtime_pay)}</span>
                  </div>
                )}
                {selectedPayslip.holiday_pay > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Holiday Pay</span>
                    <span>{formatCurrency(selectedPayslip.holiday_pay)}</span>
                  </div>
                )}
                {selectedPayslip.bonus > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Bonus</span>
                    <span>{formatCurrency(selectedPayslip.bonus)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold border-t pt-2">
                  <span>Gross Pay</span>
                  <span>{formatCurrency(selectedPayslip.gross_pay)}</span>
                </div>
              </div>
              
              <div className="bg-red-50 rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-red-700 mb-2">Deductions</h4>
                {selectedPayslip.sss > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>SSS</span>
                    <span>{formatCurrency(selectedPayslip.sss)}</span>
                  </div>
                )}
                {selectedPayslip.philhealth > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>PhilHealth</span>
                    <span>{formatCurrency(selectedPayslip.philhealth)}</span>
                  </div>
                )}
                {selectedPayslip.pagibig > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Pag-IBIG</span>
                    <span>{formatCurrency(selectedPayslip.pagibig)}</span>
                  </div>
                )}
                {selectedPayslip.tax > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Tax</span>
                    <span>{formatCurrency(selectedPayslip.tax)}</span>
                  </div>
                )}
                {selectedPayslip.cash_advance > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center gap-1">
                      Cash Advance
                      {selectedPayslip.ca_deduction_recorded && (
                        <CheckCircle size={12} className="text-green-600" />
                      )}
                    </span>
                    <span>{formatCurrency(selectedPayslip.cash_advance)}</span>
                  </div>
                )}
                {selectedPayslip.cash_advance > 0 && selectedPayslip.cash_advance_remaining !== undefined && selectedPayslip.cash_advance_remaining !== null && (
                  <div className="mt-1 mb-2 ml-3 flex justify-between text-xs text-amber-700 italic">
                    <span>↳ Remaining CA balance</span>
                    <span className="font-semibold">{formatCurrency(selectedPayslip.cash_advance_remaining)}</span>
                  </div>
                )}
                {selectedPayslip.other_deductions > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Other Deductions</span>
                    <span>{formatCurrency(selectedPayslip.other_deductions)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-red-700 border-t pt-2">
                  <span>Total Deductions</span>
                  <span>{formatCurrency(selectedPayslip.total_deductions)}</span>
                </div>
              </div>
              
              <div className="bg-green-100 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-lg">NET PAY</span>
                  <span className="font-bold text-2xl text-green-700">{formatCurrency(selectedPayslip.net_pay)}</span>
                </div>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowViewDialog(false)}>
                  Close
                </Button>
                <Button className="flex-1" onClick={() => window.print()}>
                  <Printer size={16} className="mr-2" />
                  Print
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payslips;
